<template>
  <v-container class="pt-1">
    <v-banner outlined v-if="!isInWebViewContext" rounded class="text-center my-1 error--text">
      Registry bridge is not connected
    </v-banner>

    <PackageCatalog
      class="mt-2"
      :command-targets="commandTargets"
      :selected-revit-versions="selectedRevitVersions"
      @debug-changed="packageDebug = $event"
    />

    <v-card outlined class="mt-2">
      <v-card-subtitle>Revit instances</v-card-subtitle>
      <v-card-text>
        <v-alert v-if="revitInstances.length === 0" dense outlined type="info">
          No Revit instances are currently registered.
        </v-alert>
        <v-list dense>
          <v-list-item v-for="instance in revitInstances" :key="instance.instanceId">
            <v-list-item-action>
              <v-checkbox v-model="selectedInstanceIds" :value="instance.instanceId" :disabled="!canRunOn(instance)" />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ instance.documentTitle || "No active document" }}
                <v-chip
                  v-if="instance.revitVersion && instance.revitVersion !== 'unknown'"
                  x-small
                  outlined
                  color="primary"
                  class="ml-2"
                >
                  Revit {{ instance.revitVersion }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ instance.machineName || "Local" }} {{ instance.windowsUser || "" }} / {{ instance.status }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn v-if="instance.status === 'online'" small outlined @click="connectInstance(instance.instanceId)">
                Connect
              </v-btn>
              <v-btn
                v-else-if="instance.status === 'connected' && !instance.direct"
                small
                outlined
                @click="disconnectInstance(instance.instanceId)"
              >
                Disconnect
              </v-btn>
              <v-chip v-else small>{{ instance.status }}</v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-alert v-if="lastPackageResult" dense outlined :type="lastPackageResult.ok ? 'success' : 'error'" class="mt-2">
      {{ packageResultText }}
    </v-alert>

    <v-expansion-panels v-if="lastPackageResult" flat class="mt-1">
      <v-expansion-panel>
        <v-expansion-panel-header class="caption">Debug — last Revit response</v-expansion-panel-header>
        <v-expansion-panel-content>
          <pre
            class="caption"
            style="
              white-space: pre-wrap;
              word-break: break-all;
              max-height: 240px;
              overflow: auto;
              background: rgba(255, 255, 255, 0.04);
              padding: 8px;
            "
            >{{ JSON.stringify(lastPackageResult, null, 2) }}</pre
          >
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-expansion-panels v-if="packageDebug && packageDebug.xanoApiBase" flat class="mt-1">
      <v-expansion-panel>
        <v-expansion-panel-header class="caption">Debug — Xano + run-package payload</v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="caption mb-1"><strong>GET</strong> {{ packageDebug.xanoApiBase }}</div>
          <pre
            class="caption"
            style="
              white-space: pre-wrap;
              word-break: break-all;
              max-height: 240px;
              overflow: auto;
              background: rgba(255, 255, 255, 0.04);
              padding: 8px;
            "
            >{{ packageDebug.xanoRaw || "(no response yet)" }}</pre
          >
          <div class="caption mt-2 mb-1"><strong>Mapped packages</strong> (after frontend normalization):</div>
          <pre
            class="caption"
            style="
              white-space: pre-wrap;
              word-break: break-all;
              max-height: 200px;
              overflow: auto;
              background: rgba(255, 255, 255, 0.04);
              padding: 8px;
            "
            >{{ JSON.stringify(packageDebug.packages, null, 2) }}</pre
          >
          <div class="caption mt-2 mb-1"><strong>Last run-package request</strong> sent to Revit:</div>
          <pre
            class="caption"
            style="
              white-space: pre-wrap;
              word-break: break-all;
              max-height: 200px;
              overflow: auto;
              background: rgba(255, 255, 255, 0.04);
              padding: 8px;
            "
            >{{
              packageDebug.lastRunRequest ? JSON.stringify(packageDebug.lastRunRequest, null, 2) : "(nothing sent yet)"
            }}</pre
          >
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card outlined class="mt-2" height="430px">
      <v-card-subtitle>Selected Elements</v-card-subtitle>
      <v-card-text>
        <v-select
          v-model="activeSelectionInstanceId"
          :items="selectionSources"
          item-text="label"
          item-value="instanceId"
          dense
          outlined
          label="Selection source"
        />
        <v-list max-height="300px" style="overflow-y: scroll">
          <div v-for="el in elementGuids" :key="el.id">
            <v-list-item dense class="caption">
              <v-list-item-content>
                <v-list-item-title v-text="el.name" />
                <v-list-item-subtitle v-text="el.id" />
              </v-list-item-content>
            </v-list-item>
            <v-divider />
          </div>
        </v-list>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import PackageCatalog from "@/components/PackageCatalog.vue";
import {
  DIRECT_INSTANCE_ID,
  WV2EVENTS,
  connectRegistryInstance,
  disconnectRegistryInstance,
  getDirectBridgeInstance,
  isInWebViewContext,
  requestRevitInstanceList,
  subscribeToWebView2Event,
  unsubscribeToWebView2Event,
} from "@/utils/webview2";

export default {
  name: "RevitDemo",
  components: { PackageCatalog },
  mounted() {
    subscribeToWebView2Event(WV2EVENTS.SelectionChanged, this.setElementGuids);
    subscribeToWebView2Event(WV2EVENTS.PackageExecutionCompleted, this.setPackageExecutionResult);
    subscribeToWebView2Event(WV2EVENTS.RevitInstanceList, this.setRevitInstances);
    subscribeToWebView2Event(WV2EVENTS.ConnectionStatus, this.setConnectionStatus);
    subscribeToWebView2Event(WV2EVENTS.RegistryStatus, this.setRegistryStatus);
    this.refreshDirectInstance();
    requestRevitInstanceList();
  },
  data() {
    return {
      registryInstances: [],
      selectedInstanceIds: [],
      activeSelectionInstanceId: null,
      selectionsByInstance: {},
      isInWebViewContext: isInWebViewContext(),
      lastPackageResult: null,
      packageDebug: null,
    };
  },
  computed: {
    revitInstances() {
      const direct = getDirectBridgeInstance();
      return direct ? [direct, ...this.registryInstances] : this.registryInstances;
    },
    commandTargets() {
      return this.selectedInstanceIds.filter((id) => {
        const instance = this.revitInstances.find((x) => x.instanceId === id);
        return instance && this.canRunOn(instance);
      });
    },
    selectedRevitVersions() {
      const versions = this.commandTargets
        .map((id) => this.revitInstances.find((x) => x.instanceId === id))
        .map((instance) => (instance && instance.revitVersion ? String(instance.revitVersion) : null))
        .filter((v) => v && v !== "unknown");
      return [...new Set(versions)];
    },
    elementGuids() {
      return this.selectionsByInstance[this.activeSelectionInstanceId] || [];
    },
    selectionSources() {
      return this.revitInstances
        .filter((instance) => this.selectionsByInstance[instance.instanceId])
        .map((instance) => ({
          instanceId: instance.instanceId,
          label: `${instance.documentTitle || instance.instanceId} / ${instance.machineName || "Local"}`,
        }));
    },
    packageResultText() {
      if (!this.lastPackageResult) {
        return "";
      }

      const instance = this.revitInstances.find((x) => x.instanceId === this.lastPackageResult.instanceId);
      const target = instance ? ` on ${instance.documentTitle || instance.machineName || instance.instanceId}` : "";

      if (!this.lastPackageResult.ok) {
        return `Package failed${target}: ${this.lastPackageResult.error || "unknown error"}`;
      }

      const result = this.lastPackageResult.result || {};
      const name = result.name ? `: ${result.name}` : "";
      return `Package completed${target}${name}`;
    },
  },
  methods: {
    refreshDirectInstance() {
      const direct = getDirectBridgeInstance();
      if (direct && !this.selectedInstanceIds.includes(DIRECT_INSTANCE_ID)) {
        this.selectedInstanceIds.push(DIRECT_INSTANCE_ID);
      }
    },
    canRunOn(instance) {
      return instance.status === "connected" || instance.instanceId === DIRECT_INSTANCE_ID;
    },
    connectInstance(instanceId) {
      connectRegistryInstance(instanceId);
    },
    disconnectInstance(instanceId) {
      disconnectRegistryInstance(instanceId);
      this.selectedInstanceIds = this.selectedInstanceIds.filter((id) => id !== instanceId);
    },
    setRegistryStatus() {
      this.isInWebViewContext = isInWebViewContext();
      this.refreshDirectInstance();
    },
    setRevitInstances(e) {
      this.registryInstances = e.detail || [];
      const allowed = new Set(this.revitInstances.filter((x) => this.canRunOn(x)).map((x) => x.instanceId));
      this.selectedInstanceIds = this.selectedInstanceIds.filter((id) => allowed.has(id));
    },
    setConnectionStatus(e) {
      const status = e.detail || {};
      this.registryInstances = this.registryInstances.map((instance) =>
        instance.instanceId === status.instanceId ? { ...instance, status: status.status } : instance
      );
    },
    setElementGuids(e) {
      const detail = e.detail || {};
      const instanceId = detail.instanceId || DIRECT_INSTANCE_ID;
      const elements = Array.isArray(detail) ? detail : detail.elements || [];
      this.$set(this.selectionsByInstance, instanceId, elements);
      if (!this.activeSelectionInstanceId) {
        this.activeSelectionInstanceId = instanceId;
      }
    },
    setPackageExecutionResult(e) {
      this.lastPackageResult = e.detail;
    },
  },
  beforeDestroy() {
    unsubscribeToWebView2Event(WV2EVENTS.SelectionChanged, this.setElementGuids);
    unsubscribeToWebView2Event(WV2EVENTS.PackageExecutionCompleted, this.setPackageExecutionResult);
    unsubscribeToWebView2Event(WV2EVENTS.RevitInstanceList, this.setRevitInstances);
    unsubscribeToWebView2Event(WV2EVENTS.ConnectionStatus, this.setConnectionStatus);
    unsubscribeToWebView2Event(WV2EVENTS.RegistryStatus, this.setRegistryStatus);
  },
};
</script>

<style scoped></style>
